import React, { createContext, useCallback, useContext, useState } from 'react';

import { ConversationEntityType } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { ConversationSection } from '~/types/api.graphql';

const DEFAULT_SIDEBAR_WIDTH = 400;
const MIN_SIDEBAR_WIDTH = 280;
const MAX_SIDEBAR_WIDTH = 600;

const readStoredWidth = (): number => {
  const stored = localStorage.getItem(visitIQAiInsightsSidebarWidth);
  if (!stored) return DEFAULT_SIDEBAR_WIDTH;
  const parsed = parseInt(stored, 10);
  return isNaN(parsed) ? DEFAULT_SIDEBAR_WIDTH : Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, parsed));
};

export { DEFAULT_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH };

interface SidebarOpenParams {
  section?: ConversationSection;
  entityId?: string;
  entityType?: ConversationEntityType;
}

interface AIInsightsSidebarContextValue {
  isOpen: boolean;
  section: ConversationSection | null;
  entityId: string | null;
  entityType: ConversationEntityType | null;
  sidebarWidth: number;
  isResizing: boolean;
  conversationId: string | null;
  clickCounterCTA: number;
  setSidebarWidth: (width: number) => void;
  setIsResizing: (resizing: boolean) => void;
  setConversationId: (id: string | null) => void;
  openSidebar: (params: SidebarOpenParams) => void;
  closeSidebar: () => void;
}

const AIInsightsSidebarContext = createContext<AIInsightsSidebarContextValue | null>(null);

export const AIInsightsSidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [section, setSection] = useState<ConversationSection | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<ConversationEntityType | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState<number>(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // to know that a CTA for same section is clicked
  const [clickCounterCTA, setClickCounterCTA] = useState(0);

  const openSidebar = useCallback(
    ({ section = null, entityId = null, entityType = null }: SidebarOpenParams) => {
      setSection(section);
      setEntityId(entityId);
      setEntityType(entityType);
      if (!isOpen) {
        setConversationId(null);
      }
      setIsOpen(true);
      setClickCounterCTA((value) => value + 1);
    },
    [isOpen],
  );

  const closeSidebar = useCallback(() => {
    setConversationId(null);
    setIsOpen(false);
  }, []);

  return (
    <AIInsightsSidebarContext.Provider
      value={{
        isOpen,
        section,
        entityId,
        entityType,
        sidebarWidth,
        isResizing,
        conversationId,
        clickCounterCTA,
        setSidebarWidth,
        setIsResizing,
        setConversationId,
        openSidebar,
        closeSidebar,
      }}
    >
      {children}
    </AIInsightsSidebarContext.Provider>
  );
};

export const useAIInsightsSidebar = () => useContext(AIInsightsSidebarContext);
